import React, { useState, useEffect, useCallback } from 'react';
import { Globe, MapPin, Wifi, Crosshair, ShieldCheck, X, RefreshCw, Compass, AlertCircle } from 'lucide-react';
import useAnalytics from '../hooks/useAnalytics';
import './VisitorLocationModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Reverse geocode GPS coordinates → city/region/country using Nominatim (free, no key).
 */
const reverseGeocode = async (latitude, longitude) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en`,
      { headers: { 'User-Agent': 'PortfolioTelemetry/1.0' } }
    );
    if (!res.ok) throw new Error('Nominatim error');
    const data = await res.json();
    const addr = data.address || {};
    return {
      city: addr.city || addr.town || addr.village || addr.suburb || addr.county || 'Unknown City',
      region: addr.state || addr.state_district || 'Unknown Region',
      country: addr.country || 'Unknown Country',
      countryCode: (addr.country_code || 'UN').toUpperCase(),
      displayName: data.display_name || ''
    };
  } catch (err) {
    console.warn('Reverse geocode failed:', err.message);
    return null;
  }
};

export default function VisitorLocationModal({ isOpen, onClose }) {
  const { updateLocation } = useAnalytics();
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gpsData, setGpsData] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  // Reverse-geocoded location when GPS is active
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsLocationLoading, setGpsLocationLoading] = useState(false);

  const fetchIPLocation = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/location`);
      const data = await res.json();
      if (data.success) {
        setLocationData(data);
      } else {
        throw new Error(data.message || 'Failed to resolve location');
      }
    } catch (err) {
      console.error('Location fetch error:', err);
      // Fallback direct client lookup if backend is unavailable
      try {
        const clientRes = await fetch('https://ipapi.co/json/');
        const clientData = await clientRes.json();
        setLocationData({
          ip: clientData.ip,
          city: clientData.city,
          region: clientData.region,
          country: clientData.country_name,
          countryCode: clientData.country_code,
          isp: clientData.org || clientData.asn,
          latitude: clientData.latitude,
          longitude: clientData.longitude,
          timezone: clientData.timezone,
          source: 'Client Direct'
        });
      } catch (fallbackErr) {
        setError('Unable to fetch IP location');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply GPS coords + trigger reverse geocoding to update the location card.
   */
  const applyGpsData = useCallback(async (coords) => {
    setGpsData(coords);
    setGpsLocation(null);
    setGpsLocationLoading(true);
    const geo = await reverseGeocode(coords.latitude, coords.longitude);
    if (geo) setGpsLocation(geo);
    setGpsLocationLoading(false);

    if (updateLocation) {
      updateLocation({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
        city: geo?.city,
        region: geo?.region,
        country: geo?.country,
        countryCode: geo?.countryCode,
        isGps: true
      });
    }
  }, [updateLocation]);

  useEffect(() => {
    if (!isOpen) return;

    fetchIPLocation();

    // Auto-apply any GPS coords already captured from the LocationPromptModal
    let appliedFromStorage = false;
    try {
      const saved = localStorage.getItem('cyber_gps_data');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.latitude && parsed.longitude) {
          applyGpsData(parsed);
          appliedFromStorage = true;
        }
      }
    } catch (_) {}

    // If browser geolocation permission is already GRANTED, auto-fire GPS immediately
    // so correct location shows on first open — no button click needed
    const autoTriggerGpsIfGranted = async () => {
      if (!navigator.geolocation) return;
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        if (result.state === 'granted') {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude, accuracy } = position.coords;
              const coords = {
                latitude,
                longitude,
                accuracy: Math.round(accuracy),
                timestamp: new Date(position.timestamp).toLocaleTimeString()
              };
              // Save to localStorage so future opens are instant too
              try { localStorage.setItem('cyber_gps_data', JSON.stringify(coords)); } catch (_) {}
              await applyGpsData(coords);
            },
            () => { /* silently fall back to IP/stored data */ },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
          );
        }
      } catch (_) {
        // Permissions API not supported — rely on localStorage fallback
      }
    };
    autoTriggerGpsIfGranted();

    // Also listen for GPS captured while this modal is already open
    const handleGpsCaptured = (e) => {
      if (e.detail && e.detail.latitude && e.detail.longitude) {
        applyGpsData(e.detail);
      }
    };
    window.addEventListener('gps_location_captured', handleGpsCaptured);
    return () => window.removeEventListener('gps_location_captured', handleGpsCaptured);
  }, [isOpen, applyGpsData]);

  const handleGetExactGPS = () => {
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setGpsError('Satellite GPS requires HTTPS (Secure Origin). Access via https:// or localhost.');
      return;
    }

    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser');
      return;
    }

    setGpsLoading(true);
    setGpsError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const coords = {
          latitude,
          longitude,
          accuracy: Math.round(accuracy),
          timestamp: new Date(position.timestamp).toLocaleTimeString()
        };
        setGpsLoading(false);
        await applyGpsData(coords);
      },
      (err) => {
        console.warn('GPS error:', err);
        if (err.message && err.message.toLowerCase().includes('secure origin')) {
          setGpsError('Satellite GPS requires HTTPS (Secure Origin). Access via https:// or localhost.');
        } else {
          setGpsError(err.message || 'Location permission denied or unavailable');
        }
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  if (!isOpen) return null;

  // Active coordinates (GPS preferred over IP)
  const activeLat = gpsData ? gpsData.latitude : (locationData?.latitude || 0);
  const activeLng = gpsData ? gpsData.longitude : (locationData?.longitude || 0);
  const isGpsActive = Boolean(gpsData);

  // Active location display: GPS reverse-geocoded location takes priority over IP-based
  const displayCity    = isGpsActive ? (gpsLocation?.city    ?? locationData?.city)    : locationData?.city;
  const displayRegion  = isGpsActive ? (gpsLocation?.region  ?? locationData?.region)  : locationData?.region;
  const displayCountry = isGpsActive ? (gpsLocation?.country ?? locationData?.country) : locationData?.country;
  const displayCode    = isGpsActive ? (gpsLocation?.countryCode ?? locationData?.countryCode) : locationData?.countryCode;

  // Map Embed Bounding Box
  const delta = 0.03;
  const bbox = `${activeLng - delta},${activeLat - delta},${activeLng + delta},${activeLat + delta}`;
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${activeLat},${activeLng}`;

  return (
    <div className="cyber-modal-overlay" onClick={onClose}>
      <div className="cyber-telemetry-hud" onClick={(e) => e.stopPropagation()}>
        
        {/* HUD Header */}
        <div className="hud-header">
          <div className="hud-title">
            <Globe className="hud-icon neon-pulse" />
            <div>
              <h3>VISITOR TELEMETRY &amp; LOCATION</h3>
              <span className="hud-subtitle">SYS_GEODATA_RECON_V2.6</span>
            </div>
          </div>
          <button className="hud-close-btn" onClick={onClose} aria-label="Close telemetry HUD">
            <X size={20} />
          </button>
        </div>

        {/* HUD Content */}
        <div className="hud-body">
          {loading ? (
            <div className="hud-loading">
              <RefreshCw className="spin-icon" size={32} />
              <p>RESOLVING CYBER TELEMETRY &amp; NETWORK IP...</p>
            </div>
          ) : error ? (
            <div className="hud-error">
              <AlertCircle size={28} />
              <p>{error}</p>
              <button className="cyber-btn-sec" onClick={fetchIPLocation}>Retry</button>
            </div>
          ) : (
            <>
              {/* Telemetry Grid */}
              <div className="telemetry-grid">
                
                {/* IP Address Card */}
                <div className="telemetry-card highlight">
                  <div className="card-label">
                    <Wifi size={14} /> PUBLIC IP ADDRESS
                  </div>
                  <div className="card-value ip-text">{locationData?.ip || '0.0.0.0'}</div>
                  <div className="card-sub">{locationData?.isp || 'Detected Provider'}</div>
                </div>

                {/* Location Card — updates to GPS reverse-geocoded address when GPS is active */}
                <div className="telemetry-card">
                  <div className="card-label">
                    <MapPin size={14} />
                    {isGpsActive ? ' GPS LOCATION' : ' GEOGRAPHIC LOCATION'}
                  </div>
                  <div className="card-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {gpsLocationLoading ? (
                      <span style={{ color: '#00e5ff', fontSize: '0.85rem' }}>
                        <RefreshCw size={13} className="spin-icon" style={{ display: 'inline', marginRight: 4 }} />
                        Resolving...
                      </span>
                    ) : (
                      <>{displayCity}, {displayRegion}</>
                    )}
                  </div>
                  <div className="card-sub">
                    {gpsLocationLoading ? '' : `${displayCountry} (${displayCode})`}
                    {isGpsActive && !gpsLocationLoading && (
                      <span style={{ color: '#00ff66', marginLeft: 6, fontSize: '0.65rem' }}>● GPS</span>
                    )}
                  </div>
                </div>

                {/* Coordinates Card */}
                <div className="telemetry-card">
                  <div className="card-label">
                    <Compass size={14} /> {isGpsActive ? 'TARGET GPS COORDS' : 'ESTIMATED COORDS'}
                  </div>
                  <div className="card-value coords-text">
                    {activeLat.toFixed(4)}°, {activeLng.toFixed(4)}°
                  </div>
                  <div className="card-sub">
                    {isGpsActive ? `GPS Precision ±${gpsData.accuracy}m` : `IP Geolocation (~10km)`}
                  </div>
                </div>

                {/* Security Status Card */}
                <div className="telemetry-card">
                  <div className="card-label">
                    <ShieldCheck size={14} /> CONNECTION STATUS
                  </div>
                  <div className="card-value status-secure">SECURED</div>
                  <div className="card-sub">TLS 1.3 / Port 443</div>
                </div>

              </div>

              {/* High-Accuracy GPS Action Bar */}
              <div className="gps-action-bar">
                <div className="gps-info">
                  <div className="gps-mode-badge">
                    {isGpsActive ? '🎯 GPS HIGH-PRECISION MODE ACTIVE' : '🌐 IP GEOLOCATION MODE'}
                  </div>
                  {gpsError && <span className="gps-error-msg">{gpsError}</span>}
                </div>

                <button 
                  className={`cyber-gps-btn ${isGpsActive ? 'active' : ''}`}
                  onClick={handleGetExactGPS}
                  disabled={gpsLoading || gpsLocationLoading}
                >
                  <Crosshair className={gpsLoading ? 'spin-icon' : ''} size={16} />
                  {gpsLoading ? 'LOCKING SATELLITES...' : isGpsActive ? 'REFRESH EXACT GPS' : 'PINPOINT EXACT GPS'}
                </button>
              </div>

              {/* Live Radar Map Embed */}
              <div className="map-frame-container">
                <div className="map-header">
                  <span>LIVE GEOSPATIAL MAP FEED</span>
                  <span className="map-coords">{activeLat.toFixed(5)} N, {activeLng.toFixed(5)} E</span>
                </div>
                {activeLat !== 0 && activeLng !== 0 ? (
                  <iframe
                    title="Visitor Location Map"
                    src={mapUrl}
                    className="cyber-map-iframe"
                    loading="lazy"
                  />
                ) : (
                  <div className="map-fallback">Coordinates Unavailable</div>
                )}
              </div>
            </>
          )}
        </div>

        {/* HUD Footer */}
        <div className="hud-footer">
          <span>PORTFOLIO_TELEMETRY // ANONYMIZED_VISITOR_METRICS</span>
          <button className="hud-close-btn-sec" onClick={onClose}>CLOSE HUD</button>
        </div>

      </div>
    </div>
  );
}
